/**
 * Product Detail Page - Todo en Local Marketplace
 * Supports restaurant products (Comanda Pro) with modifiers and combos
 */

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Heart, Share2, MapPin, Store, Truck, Package, Star,
  Shield, Clock, Plus, Minus, ShoppingCart, ChevronRight, Phone, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { getProductImage } from '../../utils/constants';
import api from '../../services/api';
import { getErrorMessage } from '../../utils/errorUtils';
import VerifiedBadge, { VerifiedStoreName } from '../../components/marketplace/VerifiedBadge';
import { MarketplaceHeader, MarketplaceFooter, FloatingCartButton, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';

// Brand Logo URL
const BRAND_LOGO = '/logo.png';

// Brand colors - Professional & Modern
const COLORS = {
  navyBlue: '#1e3a5f',      // Azul marino profundo
  darkSlate: '#0f172a',     // Slate muy oscuro
  orange: '#F37322',        // Naranja - CTA principal
  lightGray: '#f8fafc',
  white: '#ffffff'
};

// ==================== MODIFIER GROUP PICKER ====================
const ModifierGroupPicker = ({ group, groupIndex, selections, onSelect }) => {
  const isRadio = group.max_selections === 1;
  const isRequired = group.min_selections > 0;
  const selected = selections[groupIndex] || [];

  const ruleText = isRequired
    ? (isRadio ? 'Elige 1' : `Elige ${group.min_selections}${group.max_selections > group.min_selections ? ` a ${group.max_selections}` : ''}`)
    : (isRadio ? 'Opcional' : `Hasta ${group.max_selections}`);

  const handleToggle = (option) => {
    if (isRadio) {
      onSelect(groupIndex, [option]);
    } else {
      const exists = selected.find(s => s.name === option.name);
      if (exists) {
        onSelect(groupIndex, selected.filter(s => s.name !== option.name));
      } else if (selected.length < group.max_selections) {
        onSelect(groupIndex, [...selected, option]);
      }
    }
  };

  const isInvalid = isRequired && selected.length < group.min_selections;

  return (
    <div className={`p-4 rounded-xl border-2 ${isInvalid ? 'border-red-200 bg-red-50/30' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-800">{group.name}</h4>
        <span className={`text-xs px-2 py-1 rounded-full font-medium ${isRequired ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`}>
          {ruleText}
        </span>
      </div>
      <div className="space-y-2">
        {(group.options || []).map((option) => {
          const isSelected = selected.some(s => s.name === option.name);
          return (
            <label
              key={option.name}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                isSelected ? 'bg-green-50 border border-green-400' : 'bg-slate-50 border border-transparent hover:bg-slate-100'
              }`}
            >
              <input
                type={isRadio ? 'radio' : 'checkbox'}
                name={`mod-group-${groupIndex}`}
                checked={isSelected}
                onChange={() => handleToggle({ name: option.name, price_adjustment: option.price_adjustment || 0 })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-${isRadio ? 'full' : 'md'} border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'border-green-500 bg-green-500' : 'border-slate-300'
              }`}>
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="flex-1 text-slate-700">{option.name}</span>
              {option.price_adjustment > 0 && (
                <span className="text-sm text-green-700 font-medium">+${option.price_adjustment.toFixed(2)}</span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ==================== COMBO SLOT PICKER ====================
const ComboSlotPicker = ({ slot, slotIndex, selection, onSelect }) => {
  return (
    <div className="p-4 rounded-xl border-2 border-slate-200 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-slate-800">{slot.name}</h4>
        {slot.required && (
          <span className="text-xs px-2 py-1 rounded-full font-medium bg-purple-100 text-purple-700">
            Obligatorio
          </span>
        )}
      </div>
      <div className="space-y-2">
        {(slot.options || []).map((option) => {
          const isSelected = selection?.product_id === option.product_id;
          return (
            <label
              key={option.product_id}
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                isSelected ? 'bg-green-50 border-2 border-green-500' : 'bg-slate-50 border-2 border-transparent hover:bg-slate-100'
              }`}
            >
              <input
                type="radio"
                name={`combo-slot-${slotIndex}`}
                checked={isSelected}
                onChange={() => onSelect(slotIndex, {
                  product_id: option.product_id,
                  product_name: option.product_name,
                  price_adjustment: option.price_adjustment || 0,
                  slot_name: slot.name,
                })}
                className="sr-only"
              />
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected ? 'border-green-500 bg-green-500' : 'border-slate-300'
              }`}>
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
              <span className="flex-1 text-slate-700">{option.product_name}</span>
              {option.price_adjustment > 0 && (
                <span className="text-sm text-green-700 font-medium">+${option.price_adjustment.toFixed(2)}</span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
};

// ==================== PRODUCT DETAIL PAGE ====================
const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [store, setStore] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [reviews, setReviews] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [cartInfo, setCartInfo] = useState({ itemCount: 0, total: 0 });

  // Favorites
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  // Restaurant/modifier state
  const [modifierSelections, setModifierSelections] = useState({});
  const [comboSelections, setComboSelections] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
    getUserLocation();
    loadCartInfo();
    loadFavoriteStatus();
  }, []);

  useEffect(() => {
    if (productId) {
      loadProduct();
    }
  }, [productId, userLocation]);

  const loadCartInfo = async () => {
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) return;

    try {
      const response = await api.get(`/marketplace/cart?customer_token=${customerToken}`);
      setCartInfo({
        itemCount: response.data.items?.length || 0,
        total: response.data.total || 0
      });
    } catch (error) {
      // Silently fail - cart info is optional
    }
  };

  const loadFavoriteStatus = async () => {
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken || !productId) return;
    try {
      const response = await api.get(`/marketplace/favorites?customer_token=${customerToken}`);
      const isFav = response.data.some(f => (f.product?.id || f.product_id) === productId);
      setIsFavorite(isFav);
    } catch (error) {
      // Silently fail
    }
  };

  const toggleFavorite = async () => {
    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) {
      toast.info('Inicia sesion para guardar favoritos');
      navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }
    setFavLoading(true);
    try {
      if (isFavorite) {
        const favResponse = await api.get(`/marketplace/favorites?customer_token=${customerToken}`);
        const favToRemove = favResponse.data.find(f => (f.product?.id || f.product_id) === productId);
        if (favToRemove) {
          await api.delete(`/marketplace/favorites/${favToRemove.favorite_id}?customer_token=${customerToken}`);
          setIsFavorite(false);
          toast.success('Eliminado de favoritos');
        }
      } else {
        await api.post(`/marketplace/favorites?customer_token=${customerToken}`, {
          product_id: productId,
          business_id: store?.id
        });
        setIsFavorite(true);
        toast.success('Agregado a favoritos');
      }
    } catch (error) {
      toast.error('Error al actualizar favoritos');
    } finally {
      setFavLoading(false);
    }
  };

  const getUserLocation = () => {
    // Try localStorage first
    const cached = localStorage.getItem('userLocation');
    if (cached) {
      try {
        setUserLocation(JSON.parse(cached));
        return;
      } catch (e) {}
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          localStorage.setItem('userLocation', JSON.stringify(loc));
        },
        () => {}
      );
    }
  };

  const loadProduct = async () => {
    try {
      setLoading(true);
      let url = `/marketplace/products/${productId}`;
      if (userLocation) {
        url += `?lat=${userLocation.lat}&lng=${userLocation.lng}`;
      }

      const response = await api.get(url);
      setProduct(response.data.product);
      setStore(response.data.store);
      setAvailability(response.data.availability || []);
      setReviews(response.data.reviews || { items: [], stats: {} });

      if (response.data.availability?.length > 0) {
        setSelectedBranch(response.data.availability[0]);
      }

      // Reset selections when product changes
      setModifierSelections({});
      setComboSelections({});
      setSpecialInstructions('');
    } catch (error) {
      console.error('Error loading product:', error);
      toast.error(getErrorMessage(error, 'Error al cargar el producto'));
    } finally {
      setLoading(false);
    }
  };

  // Derived product flags
  const isRestaurant = product?.source === 'comanda_pro';
  const hasModifiers = (product?.modifier_groups?.length || 0) > 0;
  const isCombo = !!product?.is_combo;

  // Dynamic price calculation
  const compareAtPrice = product?.compare_at_price || product?.pricing?.compare_at_price || null;
  const basePrice = isCombo
    ? (product?.combo_price || product?.price || 0)
    : (product?.price || product?.retail_price || 0);
  const modAdjustment = Object.values(modifierSelections)
    .flat()
    .reduce((s, o) => s + (o.price_adjustment || 0), 0);
  const comboAdjustment = Object.values(comboSelections)
    .reduce((s, sel) => s + (sel?.price_adjustment || 0), 0);
  const price = basePrice + modAdjustment + comboAdjustment;

  // Validation
  const modifiersValid = !hasModifiers || (product?.modifier_groups || []).every((group, idx) => {
    if (group.min_selections <= 0) return true;
    return (modifierSelections[idx] || []).length >= group.min_selections;
  });
  const comboValid = !isCombo || (product?.combo_slots || []).every((slot, idx) => {
    if (!slot.required) return true;
    return !!comboSelections[idx];
  });
  const canAddToCart = selectedBranch && selectedBranch.stock >= 1 && modifiersValid && comboValid;

  const handleAddToCart = async () => {
    if (!selectedBranch) {
      toast.error('Selecciona una sucursal');
      return;
    }
    if (!modifiersValid) {
      toast.error('Completa las opciones requeridas');
      return;
    }
    if (!comboValid) {
      toast.error('Selecciona todas las opciones del combo');
      return;
    }

    const customerToken = localStorage.getItem('customer_token');
    if (!customerToken) {
      toast.info('Inicia sesión para agregar al carrito');
      navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname));
      return;
    }

    try {
      const payload = {
        product_id: product.id,
        business_id: store.id,
        branch_id: selectedBranch.branch_id,
        quantity: quantity,
      };

      // Add modifier selections
      if (hasModifiers) {
        payload.modifier_selections = Object.entries(modifierSelections)
          .filter(([, opts]) => opts.length > 0)
          .map(([idx, opts]) => ({
            group_name: product.modifier_groups[idx].name,
            options: opts,
          }));
      }

      // Add combo selections
      if (isCombo) {
        payload.combo_selections = Object.values(comboSelections).filter(Boolean);
      }

      // Add special instructions
      if (specialInstructions.trim()) {
        payload.special_instructions = specialInstructions.trim();
      }

      await api.post(`/marketplace/cart/add?customer_token=${customerToken}`, payload);
      toast.success('Producto agregado al carrito');
      loadCartInfo();
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Error al agregar al carrito'));
    }
  };

  const [showShareMenu, setShowShareMenu] = useState(false);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = product ? `Mira este producto: ${product.name}` : '';

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: product?.name,
        text: shareText,
        url: shareUrl
      });
    } else {
      setShowShareMenu(prev => !prev);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Enlace copiado al portapapeles');
    setShowShareMenu(false);
  };

  const socialLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <MarketplaceHeader />
        <main className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
          {/* Breadcrumb skeleton */}
          <div className="flex items-center gap-2 mb-4">
            <div className="h-4 w-12 bg-slate-200 rounded animate-pulse" />
            <div className="h-4 w-4 bg-slate-200 rounded animate-pulse" />
            <div className="h-4 w-20 bg-slate-200 rounded animate-pulse" />
            <div className="h-4 w-4 bg-slate-200 rounded animate-pulse" />
            <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Image skeleton */}
            <div className="bg-white rounded-2xl overflow-hidden">
              <div className="aspect-square bg-slate-100 animate-pulse" />
            </div>
            {/* Info skeleton */}
            <div className="space-y-4">
              <div className="h-4 w-32 bg-slate-200 rounded animate-pulse" />
              <div className="h-8 w-3/4 bg-slate-200 rounded animate-pulse" />
              <div className="h-5 w-20 bg-slate-200 rounded animate-pulse" />
              <div className="h-10 w-32 bg-slate-200 rounded animate-pulse" />
              <div className="space-y-2">
                <div className="h-5 w-24 bg-slate-200 rounded animate-pulse" />
                <div className="h-4 w-full bg-slate-100 rounded animate-pulse" />
                <div className="h-4 w-5/6 bg-slate-100 rounded animate-pulse" />
                <div className="h-4 w-2/3 bg-slate-100 rounded animate-pulse" />
              </div>
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="h-5 w-28 bg-slate-200 rounded animate-pulse" />
                <div className="h-16 w-full bg-white rounded-lg border border-slate-200 animate-pulse" />
              </div>
              <div className="flex gap-3">
                <div className="h-10 w-10 bg-slate-200 rounded animate-pulse" />
                <div className="h-10 w-10 bg-slate-200 rounded animate-pulse" />
              </div>
              <div className="flex items-center gap-4">
                <div className="h-12 w-32 bg-slate-200 rounded-lg animate-pulse" />
                <div className="h-12 flex-1 bg-slate-200 rounded-xl animate-pulse" />
              </div>
            </div>
          </div>
        </main>
        <MobileBottomNav />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <Package className="w-16 h-16 text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Producto no encontrado</h2>
        <Link to="/buscar" className="mt-4 hover:underline" style={{ color: COLORS.navyBlue }}>Volver a buscar</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Shared Header */}
      <MarketplaceHeader />

      <main className="flex-1 max-w-7xl mx-auto px-4 py-6 w-full">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <Link to="/" className="hover:text-slate-700">Inicio</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/buscar" className="hover:text-slate-700">Productos</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-slate-800 truncate">{product.name}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Product Image */}
          <div className="bg-white rounded-2xl overflow-hidden">
            <img
              src={getProductImage(product.image_url)}
              alt={product.name}
              className="w-full aspect-square object-cover"
              onError={(e) => { e.target.src = getProductImage(); }}
            />
          </div>

          {/* Product Info */}
          <div>
            {/* Store badge */}
            <Link to={`/tienda/${store?.id}`} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800 mb-3">
              <Store className="w-4 h-4" />
              <VerifiedStoreName
                name={store?.name}
                verified={store?.verified}
                size="sm"
              />
            </Link>

            <div className="flex items-start justify-between gap-4">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">{product.name}</h1>
              {/* Favorite button */}
              <button
                onClick={toggleFavorite}
                disabled={favLoading}
                className={`flex-shrink-0 p-2.5 rounded-full border-2 transition-all hover:scale-110 ${
                  isFavorite ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white hover:border-red-200'
                }`}
                title={isFavorite ? 'Quitar de favoritos' : 'Agregar a favoritos'}
              >
                <Heart className={`w-5 h-5 ${isFavorite ? 'text-red-500 fill-red-500' : 'text-slate-400'}`} />
              </button>
            </div>

            {/* Badges: Restaurante / Combo / Descuento */}
            <div className="flex flex-wrap gap-2 mb-3">
              {isRestaurant && !isCombo && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700">
                  Restaurante
                </span>
              )}
              {isCombo && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                  COMBO
                </span>
              )}
              {compareAtPrice && compareAtPrice > basePrice && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-red-500 text-white">
                  -{Math.round((1 - basePrice / compareAtPrice) * 100)}% OFF
                </span>
              )}
            </div>

            {/* Rating */}
            {reviews.stats?.total > 0 && (
              <div className="flex items-center gap-2 mb-4">
                <div className="flex items-center gap-0.5">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className={`w-4 h-4 ${i <= reviews.stats.average ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200 fill-slate-200'}`} />
                  ))}
                </div>
                <span className="text-sm text-slate-600">
                  {reviews.stats.average?.toFixed(1)} ({reviews.stats.total} reseñas)
                </span>
              </div>
            )}

            {/* Price */}
            <div className="mb-6">
              <div className="flex items-baseline gap-3">
                <p className="text-3xl font-bold" style={{ color: COLORS.navyBlue }}>
                  ${price.toFixed(2)}
                </p>
                {compareAtPrice && compareAtPrice > basePrice && (
                  <p className="text-lg text-slate-400 line-through">
                    ${compareAtPrice.toFixed(2)}
                  </p>
                )}
              </div>
              {(modAdjustment > 0 || comboAdjustment > 0) && (
                <p className="text-sm text-slate-500 mt-1">
                  Base: ${basePrice.toFixed(2)}
                  {modAdjustment > 0 && <span> + modificadores: ${modAdjustment.toFixed(2)}</span>}
                  {comboAdjustment > 0 && <span> + opciones: ${comboAdjustment.toFixed(2)}</span>}
                </p>
              )}
              {product.wholesale_price && store?.wholesale_enabled && (
                <p className="text-sm text-slate-500 mt-1">
                  Precio mayoreo: ${product.wholesale_price.toFixed(2)}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="font-semibold text-slate-800 mb-2">Descripción</h3>
              <p className="text-slate-600 leading-relaxed">
                {product.description_short || product.description_full || product.description || 'Este producto no tiene descripción disponible.'}
              </p>
              {product.description_full && product.description_short && (
                <p className="text-slate-500 text-sm mt-2 leading-relaxed">
                  {product.description_full}
                </p>
              )}
            </div>

            {/* Modifier Groups */}
            {hasModifiers && (
              <div className="mb-6 space-y-3">
                <h3 className="font-semibold text-slate-800">Personaliza tu pedido</h3>
                {product.modifier_groups.map((group, idx) => (
                  <ModifierGroupPicker
                    key={idx}
                    group={group}
                    groupIndex={idx}
                    selections={modifierSelections}
                    onSelect={(groupIdx, opts) => setModifierSelections(prev => ({ ...prev, [groupIdx]: opts }))}
                  />
                ))}
              </div>
            )}

            {/* Combo Slots */}
            {isCombo && product.combo_slots?.length > 0 && (
              <div className="mb-6 space-y-3">
                <h3 className="font-semibold text-slate-800">Arma tu combo</h3>
                {product.combo_slots.map((slot, idx) => (
                  <ComboSlotPicker
                    key={idx}
                    slot={slot}
                    slotIndex={idx}
                    selection={comboSelections[idx]}
                    onSelect={(slotIdx, sel) => setComboSelections(prev => ({ ...prev, [slotIdx]: sel }))}
                  />
                ))}
              </div>
            )}

            {/* Special Instructions */}
            {isRestaurant && (
              <div className="mb-6">
                <textarea
                  placeholder="Instrucciones especiales (sin picante, bien cocido, etc.)"
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  className="w-full border rounded-xl p-3 text-sm focus:outline-none focus:border-green-500"
                  rows={2}
                />
              </div>
            )}

            {/* SKU/Barcode */}
            {(product.sku || product.barcode) && (
              <div className="flex gap-4 text-sm text-slate-500 mb-6">
                {product.sku && <span>SKU: {product.sku}</span>}
                {product.barcode && <span>Código: {product.barcode}</span>}
              </div>
            )}

            {/* Branch Selection */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-slate-800 mb-3">Disponibilidad</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availability.map((branch) => (
                  <label
                    key={branch.branch_id}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedBranch?.branch_id === branch.branch_id
                        ? 'bg-green-50 border-2 border-green-500'
                        : 'bg-white border border-slate-200 hover:border-green-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="branch"
                      checked={selectedBranch?.branch_id === branch.branch_id}
                      onChange={() => setSelectedBranch(branch)}
                      className="sr-only"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-800">{branch.branch_name}</p>
                      <p className="text-sm text-slate-500">{branch.address}</p>
                      <div className="flex items-center gap-3 mt-1 text-xs">
                        {branch.stock <= 5 ? (
                          <span className="text-red-600 font-semibold">
                            {branch.stock === 1 ? 'Ultima unidad!' : `Ultimas ${branch.stock} unidades!`}
                          </span>
                        ) : (
                          <span className="text-green-600">{branch.stock} disponibles</span>
                        )}
                        {branch.distance_km && (
                          <span className="text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {branch.distance_km} km
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Delivery options */}
            <div className="flex flex-wrap gap-3 mb-6">
              {store?.pickup_enabled && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm">
                  <Store className="w-4 h-4" /> Recoger en tienda
                </span>
              )}
              {store?.delivery_enabled && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-sm">
                  <Truck className="w-4 h-4" /> Entrega a domicilio
                </span>
              )}
              {store?.shipping_enabled && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm">
                  <Package className="w-4 h-4" /> Envío por paquetería
                </span>
              )}
            </div>

            {/* Trust signals */}
            <div className="flex flex-wrap items-center gap-4 mb-6 py-4 px-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Shield className="w-4 h-4 text-green-500" />
                <span>Compra Protegida</span>
              </div>
              {store?.verified && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Check className="w-4 h-4 text-blue-500" />
                  <span>Tienda Verificada</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Clock className="w-4 h-4 text-orange-500" />
                <span>Pago en tienda</span>
              </div>
            </div>

            {/* Low stock alert - global */}
            {selectedBranch && selectedBranch.stock <= 5 && selectedBranch.stock > 0 && (
              <div className="mb-4 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2">
                <span className="text-amber-600 text-sm font-semibold">
                  {selectedBranch.stock === 1 ? 'Ultima unidad disponible!' : `Solo quedan ${selectedBranch.stock} unidades!`}
                </span>
              </div>
            )}

            {/* Quantity & Add to cart */}
            <div className="flex items-center gap-4">
              <div className="flex items-center border rounded-lg bg-white">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-slate-100 rounded-l-lg"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center font-medium">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(selectedBranch?.stock || 99, quantity + 1))}
                  className="p-3 hover:bg-slate-100 rounded-r-lg"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={!canAddToCart}
                className="flex-1 py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all disabled:opacity-50 hover:opacity-90 active:scale-[0.98]"
                style={{ backgroundColor: COLORS.navyBlue }}
              >
                <ShoppingCart className="w-5 h-5" />
                Agregar al carrito — ${(price * quantity).toFixed(2)}
              </button>
            </div>

            {/* Sticky mobile add-to-cart */}
            <div className="fixed bottom-14 left-0 right-0 bg-white border-t border-slate-200 p-3 z-40 sm:hidden">
              <div className="flex items-center gap-3 max-w-7xl mx-auto">
                <div className="flex items-center border rounded-lg bg-white">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 hover:bg-slate-100">
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-8 text-center text-sm font-medium">{quantity}</span>
                  <button onClick={() => setQuantity(Math.min(selectedBranch?.stock || 99, quantity + 1))} className="p-2 hover:bg-slate-100">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={!canAddToCart}
                  className="flex-1 py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: COLORS.orange }}
                >
                  <ShoppingCart className="w-4 h-4" />
                  Agregar — ${(price * quantity).toFixed(2)}
                </button>
              </div>
            </div>

            {/* Estimated time */}
            {store?.avg_preparation_time && (
              <p className="text-sm text-slate-500 mt-4 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Tiempo estimado de preparación: {store.avg_preparation_time} min
              </p>
            )}

            {/* Share Section */}
            <div className="mt-6 pt-6 border-t border-slate-100">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-slate-600">Compartir:</span>
                <div className="flex items-center gap-2">
                  {/* WhatsApp */}
                  <a
                    href={socialLinks.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center hover:bg-green-600 transition-colors"
                    title="Compartir en WhatsApp"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  </a>
                  {/* Facebook */}
                  <a
                    href={socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-blue-600 flex items-center justify-center hover:bg-blue-700 transition-colors"
                    title="Compartir en Facebook"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                  {/* Twitter/X */}
                  <a
                    href={socialLinks.twitter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-black flex items-center justify-center hover:bg-gray-800 transition-colors"
                    title="Compartir en X"
                  >
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-white fill-current">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  </a>
                  {/* Telegram */}
                  <a
                    href={socialLinks.telegram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-sky-500 flex items-center justify-center hover:bg-sky-600 transition-colors"
                    title="Compartir en Telegram"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-white fill-current">
                      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                  </a>
                  {/* Copy Link */}
                  <button
                    onClick={handleCopyLink}
                    className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center hover:bg-slate-300 transition-colors"
                    title="Copiar enlace"
                  >
                    <Share2 className="w-4 h-4 text-slate-600" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Store Info Card */}
        <div className="mt-8 bg-white rounded-xl p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-slate-100 flex items-center justify-center">
                {store?.logo_url ? (
                  <img src={store.logo_url} alt={store.name} className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <Store className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <div>
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  {store?.name}
                  {store?.verified && <VerifiedBadge size="sm" />}
                </h3>
                <p className="text-sm text-slate-500">
                  {store?.business_type === 'darkstore' ? 'DarkStore' : 'Tienda Física'}
                </p>
                {store?.average_rating > 0 && (
                  <div className="flex items-center gap-1 mt-1">
                    <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                    <span className="text-sm">{store.average_rating.toFixed(1)}</span>
                    <span className="text-sm text-slate-400">({store.total_reviews} reseñas)</span>
                  </div>
                )}
              </div>
            </div>
            <Link
              to={`/tienda/${store?.id}`}
              className="px-4 py-2 border rounded-lg text-sm hover:bg-slate-50 flex items-center gap-1"
            >
              Ver tienda <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Reviews Section */}
        {reviews.items?.length > 0 && (
          <div className="mt-8 bg-white rounded-xl p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Reseñas del producto</h3>
            <div className="space-y-4">
              {reviews.items.slice(0, 5).map((review) => (
                <div key={review.id} className="border-b pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`w-4 h-4 ${i <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-200'}`} />
                      ))}
                    </div>
                    <span className="text-sm text-slate-600">{review.customer_name}</span>
                    {review.is_verified_purchase && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Compra verificada</span>
                    )}
                  </div>
                  {review.comment && <p className="text-slate-600 text-sm">{review.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Spacer for mobile sticky cart + bottom nav */}
      <div className="h-20 sm:hidden" />

      {/* Shared Footer */}
      <MarketplaceFooter />

      {/* Floating Cart Button */}
      <FloatingCartButton />
      <MobileBottomNav />
    </div>
  );
};

export default ProductDetail;
